import React, { useEffect, useCallback, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { persistor } from "../../store";
import { Types } from "../../store/reducer";
import { obterIdTenent, obterIdUsuario } from "../../services/seguranca";
import api from "../../services/api";
import { FaBuilding } from "react-icons/fa";
import { FiChevronDown, FiCheck } from "react-icons/fi";
import styled from "styled-components";

const Wrapper = styled.div`
    position: relative;
    width: 100%;
    margin-top: -8px;
    margin-bottom: 10px;
    user-select: none;
`;

const Trigger = styled.button<{ isOpen: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 14px;
    border: none;
    border-radius: 8px;
    background: linear-gradient(90deg, #0d3066 0%, #1a4a9e 100%);
    color: #ffffff;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: filter 0.2s;

    &:hover {
        filter: brightness(1.1);
    }

    .filial-icon {
        flex-shrink: 0;
        display: flex;
        align-items: center;
    }

    .filial-nome {
        flex: 1;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .chevron {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        transition: transform 0.25s ease;
        transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0deg)")};
    }
`;

const Dropdown = styled.div<{ isOpen: boolean }>`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 6px;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
    z-index: 1100;
    overflow: hidden;
    max-height: ${({ isOpen }) => (isOpen ? "320px" : "0")};
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
    transform: ${({ isOpen }) => (isOpen ? "translateY(0)" : "translateY(-6px)")};
    transition: max-height 0.25s ease, opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 10px;
    }
`;

const Option = styled.button<{ selected: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 14px;
    border: none;
    background: ${({ selected }) => (selected ? "#f0f4fa" : "transparent")};
    color: ${({ selected }) => (selected ? "#1F467E" : "#333")};
    font-size: 0.85rem;
    font-weight: ${({ selected }) => (selected ? 600 : 500)};
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;

    &:hover {
        background: rgba(58, 92, 141, 0.1);
    }

    .opt-icon {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        color: ${({ selected }) => (selected ? "#1F467E" : "#999")};
    }

    .opt-nome {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .opt-check {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        color: #1F467E;
    }
`;

export default function FilialSelector() {
    const dispatch = useAppDispatch();
    const token = useAppSelector(state => state.token);
    const filiais = useAppSelector(state => state.filiais) || [];
    const filialSelecionada = useAppSelector(state => state.filialSelecionada) || null;
    const empresaTemFiliais = useAppSelector(state => state.empresaTemFiliais) || false;
    const [filiaisDoUsuario, setFiliaisDoUsuario] = useState<{ id: string; nome: string }[]>([]);
    const [filiaisEmpresa, setFiliaisEmpresa] = useState<{ id: string; nome: string }[]>([]);
    const [carregado, setCarregado] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const carregarFiliais = useCallback(async () => {
        if (!token) return;

        try {
            // Carrega filiais da empresa (para saber se existem)
            const respEmpresa = await api.get(`/Filial/PorEmpresa/${obterIdTenent(token)}`);
            const todasFiliais = (respEmpresa.data || []).map((f: any) => ({ id: f.id, nome: f.nome }));
            setFiliaisEmpresa(todasFiliais);
            dispatch({ type: Types.SET_FILIAIS, filiais: todasFiliais });

            if (todasFiliais.length === 0) {
                setCarregado(true);
                return;
            }

            // Carrega filiais do usuário (suas associações) com detalhes
            const respUsuario = await api.get(`/Filial/PorUsuarioDetalhado/${obterIdUsuario(token)}`);
            const filiaisUserData = respUsuario.data || [];
            const filiaisUser = filiaisUserData.map((f: any) => ({ id: f.idFilial || f.id, nome: f.nomeFilial || f.nome }));
            setFiliaisDoUsuario(filiaisUser);

            // Verifica se o usuário tem filialEstrita habilitada (qualquer associação com filialEstrita = true)
            const temFilialEstrita = filiaisUserData.some((f: any) => f.filialEstrita === true);
            dispatch({ type: Types.SET_FILIAL_ESTRITA, filialEstrita: temFilialEstrita });

            // Se o usuário tem filiais atribuídas e nenhuma está selecionada, auto-selecionar a primeira e recarregar.
            // persistor.flush() garante que a seleção foi gravada ANTES do reload — sem isso, se a escrita
            // assíncrona do redux-persist não completasse a tempo, o reload reabriria sem seleção e dispararia
            // novo reload (loop).
            if (filiaisUser.length > 0 && !filialSelecionada) {
                dispatch({ type: Types.SET_FILIAL, filialId: filiaisUser[0].id });
                await persistor.flush();
                window.location.reload();
                return;
            }

            setCarregado(true);
        } catch {
            dispatch({ type: Types.SET_FILIAIS, filiais: [] });
            setCarregado(true);
        }
    }, [token, dispatch]);

    useEffect(() => {
        carregarFiliais();
    }, [carregarFiliais]);

    // Fecha o dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (!empresaTemFiliais || filiaisEmpresa.length === 0 || !carregado) {
        return null;
    }

    // Usuário com filiais atribuídas: só vê as suas, sem opção "Todas"
    const temFiliaisAtribuidas = filiaisDoUsuario.length > 0;
    const opcoesVisiveis = temFiliaisAtribuidas ? filiaisDoUsuario : filiaisEmpresa;

    const selecionar = async (filialId: string | null) => {
        setIsOpen(false);
        dispatch({ type: Types.SET_FILIAL, filialId });
        // Garante a persistência da seleção antes do reload (evita reabrir sem seleção / loop).
        await persistor.flush();
        window.location.reload();
    };

    const nomeSelecionada = filialSelecionada
        ? (opcoesVisiveis.find(f => f.id === filialSelecionada)?.nome ?? "Selecione a unidade")
        : "Todas as unidades";

    return (
        <Wrapper ref={wrapperRef}>
            <Trigger
                type="button"
                isOpen={isOpen}
                onClick={() => setIsOpen(prev => !prev)}
                title={nomeSelecionada}
            >
                <span className="filial-icon">
                    <FaBuilding size={14} />
                </span>
                <span className="filial-nome">{nomeSelecionada}</span>
                <span className="chevron">
                    <FiChevronDown size={16} />
                </span>
            </Trigger>

            <Dropdown isOpen={isOpen}>
                {!temFiliaisAtribuidas && (
                    <Option
                        type="button"
                        selected={!filialSelecionada}
                        onClick={() => selecionar(null)}
                    >
                        <span className="opt-icon"><FaBuilding size={14} /></span>
                        <span className="opt-nome">Todas as unidades</span>
                        {!filialSelecionada && <span className="opt-check"><FiCheck size={15} /></span>}
                    </Option>
                )}
                {opcoesVisiveis.map(f => {
                    const selected = filialSelecionada === f.id;
                    return (
                        <Option
                            key={f.id}
                            type="button"
                            selected={selected}
                            onClick={() => selecionar(f.id)}
                        >
                            <span className="opt-icon"><FaBuilding size={14} /></span>
                            <span className="opt-nome">{f.nome}</span>
                            {selected && <span className="opt-check"><FiCheck size={15} /></span>}
                        </Option>
                    );
                })}
            </Dropdown>
        </Wrapper>
    );
}
